import { useCallback, useMemo, useState } from "react";
import { useMediaQuery, Menu as MuiMenu, MenuItem, Divider, Breadcrumbs } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useProSidebar } from "react-pro-sidebar";
import { ChevronDown, LogOut, Menu } from "lucide-react";
import {
  colorTokens,
  typographyTokens,
  mapFontWeight,
  fontStack,
  pxValue,
} from "../../styles/designTokens";
import { menuItems } from "../../json/SideBarItems";

const backendUrl = process.env.REACT_APP_BACKEND_URL;


const formatUnitName = (name = "") =>
  name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : "";

function pageNameConvert(str = "") {
  if (!str) return "";
  return str
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const bodyBaseMedium = typographyTokens.bodyBaseMedium || {};
const userNameStyle = {
  fontFamily: bodyBaseMedium.fontFamily || fontStack,
  fontSize: pxValue(bodyBaseMedium.fontSize || 14),
  fontWeight: mapFontWeight(bodyBaseMedium.fontWeight || "Medium"),
  color: colorTokens.contentDarkPrimary || "#0f172a",
};

const UNIT_ROUTE_MAP = (() => {
  const map = {};
  menuItems.forEach((section) => {
    const sectionName = section?.name || "";
    const sectionSlug = sectionName.toLowerCase();
    (section?.item || []).forEach((item) => {
      if (item.to) {
        map[item.to] = { name: sectionName, slug: sectionSlug };
      }
      (item?.subtitle || []).forEach((sub) => {
        if (sub.to) {
          map[sub.to] = { name: sectionName, slug: sectionSlug };
        }
      });
    });
  });
  return map;
})();

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageName = location.pathname.replace("/", "");
  const { toggleSidebar } = useProSidebar();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const username = useMemo(
    () => localStorage.getItem("username") || "Usuario Kala",
    []
  );
  const avatarUrl = useMemo(() => {
    const avatarPath = localStorage.getItem("avatar");
    if (!avatarPath) return null;
    return avatarPath.startsWith("http")
      ? avatarPath
      : `${backendUrl}${avatarPath}`;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem("jwt");
    localStorage.removeItem("jwt");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("id");
    localStorage.removeItem("session");
    localStorage.removeItem("role");
    localStorage.removeItem("avatar");
    navigate(`/`, { replace: true });
  }, [navigate]);

  const handleOpenSideBar = useCallback(() => toggleSidebar(true), [toggleSidebar]);
  const handleUserMenuOpen = useCallback((e) => setUserMenuAnchor(e.currentTarget), []);
  const handleUserMenuClose = useCallback(() => setUserMenuAnchor(null), []);
  const handleLogout = useCallback(() => {
    setUserMenuAnchor(null);
    logout();
  }, [logout]);

  const rawPathSegments = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((segment) => segment && segment !== "home"),
    [location.pathname]
  );

  const unitCrumb = useMemo(() => {
    const path = location.pathname;
    let matchedKey = null;
    Object.keys(UNIT_ROUTE_MAP).forEach((routePath) => {
      if (path === routePath || path.startsWith(`${routePath}/`)) {
        if (!matchedKey || routePath.length > matchedKey.length) {
          matchedKey = routePath;
        }
      }
    });
    if (!matchedKey) return null;
    const info = UNIT_ROUTE_MAP[matchedKey];
    if (!info?.name) return null;
    return {
      label: formatUnitName(info.name),
      slug: info.slug,
    };
  }, [location.pathname]);

  const pathSegments = useMemo(() => {
    if (!unitCrumb?.slug) return rawPathSegments;
    if (!rawPathSegments.length) return rawPathSegments;
    const [first, ...rest] = rawPathSegments;
    if (first.toLowerCase() === unitCrumb.slug) {
      return rest;
    }
    return rawPathSegments;
  }, [rawPathSegments, unitCrumb]);

  const breadcrumbs = useMemo(
    () =>
      pathSegments.map((segment, index) => ({
        label: pageNameConvert(segment),
        to: `/${pathSegments.slice(0, index + 1).join("/")}`,
      })),
    [pathSegments]
  );

  return (
    <nav
      className="sticky top-0 z-20 border-b"
      style={{
        background: colorTokens.backgroundPrimary || "#ffffff",
        borderColor: colorTokens.borderPrimary || "#e2e8f0",
      }}
    >
      <div className="flex w-full items-center justify-between gap-4 px-8 h-[71px]">
        <div className="flex flex-1 items-center gap-4 min-w-0">
          <button
            onClick={handleOpenSideBar}
            className={`rounded-full border border-slate-200 text-slate-500 transition hover:text-slate-900 ${
              isMobile ? "p-2" : "hidden"
            }`}
            title="Abrir menú"
          >
            <Menu className="h-5 w-5" strokeWidth={1.8} />
          </button>

          <div className="flex flex-col gap-1 min-w-0">
            <Breadcrumbs
              separator="/"
              sx={{
                "& .MuiBreadcrumbs-separator": {
                  fontSize: "0.85rem",
                  color: colorTokens.contentDarkPrimary || "#0f172a",
                },
              }}
            >
              <Link
                to="/home"
                className="text-sm font-medium text-slate-500 hover:text-slate-900 transition"
              >
                Inicio
              </Link>
              {unitCrumb && (
                <span className="text-sm font-medium text-slate-500">
                  {unitCrumb.label}
                </span>
              )}
              {breadcrumbs.length === 0 && (
                <span className="text-sm font-semibold text-slate-900">
                  {pageNameConvert(pageName) || "Home"}
                </span>
              )}
              {breadcrumbs.map((crumb, idx) =>
                idx === breadcrumbs.length - 1 ? (
                  <span
                    key={crumb.to}
                    className="text-sm font-semibold text-slate-900"
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    key={crumb.to}
                    to={crumb.to}
                    className="text-sm font-medium text-slate-500 hover:text-slate-900 transition"
                  >
                    {crumb.label}
                  </Link>
                )
              )}
            </Breadcrumbs>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleUserMenuOpen}
            className="group flex items-center gap-3 rounded-full border border-transparent px-2 py-1 transition hover:border-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          >
            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-100">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={username}
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="text-sm font-semibold text-slate-700">
                  {username
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((word) => word[0])
                    .join("")
                    .toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex items-center leading-tight text-left max-w-[180px] gap-[15px]">
              <span className="truncate" style={userNameStyle}>
                {username}
              </span>
              <ChevronDown
                className="h-4 w-4 text-slate-900"
                strokeWidth={2}
                style={{ color: colorTokens.contentDarkPrimary || "#0f172a" }}
              />
            </div>
          </button>
        </div>
      </div>
      <MuiMenu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "8px",
            border: `1px solid ${colorTokens.borderPrimary || "#e2e8f0"}`,
            minWidth: 200,
            mt: 1,
            boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
            overflow: "hidden",
          },
        }}
      >
        <div className="px-4 py-3 flex items-center gap-3 bg-slate-50/60">
          <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white shadow-inner">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={username}
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="text-sm font-semibold text-slate-700">
                {username
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((word) => word[0])
                  .join("")
                  .toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex flex-col text-left leading-tight">
            <span className="text-sm font-semibold text-slate-900">
              {username}
            </span>
            <span className="text-xs text-slate-500">Sesión activa</span>
          </div>
        </div>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            gap: 1.5,
            mx: 1,
            my: 0.5,
            borderRadius: "6px",
            color: "rgb(71, 85, 105)",
            "&:hover": { backgroundColor: "rgba(15,23,42,0.05)" },
          }}
        >
          <LogOut className="h-4 w-4" strokeWidth={1.8} />
          Cerrar sesión
        </MenuItem>
      </MuiMenu>
    </nav>
  );
};

export default NavBar;
